import { Box, CircularProgress, Typography } from '@mui/material';
import { CategoryScale, Chart, ChartData, Filler, LineElement, LinearScale, PointElement } from 'chart.js';
import Info from 'components/Info/Info';
import { useContent } from 'contexts/Content/Content';
import { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import * as setup from 'setup';

Chart.register(
    CategoryScale,
    Filler,
    LinearScale,
    LineElement,
    PointElement,
);

export default function SimulationGraph({ loading, variant }: SimulationGraphProps) {

    const [data, setData] = useState<ChartData<'line'> | null>(null);
    const content = useContent();

    useEffect(() => {
        // Get chart data
        const newChartData = {
            datasets: variant?.datasets.map((dataset, index) => ({
                backgroundColor: setup.simulation.colors[index],
                borderColor: 'transparent',
                data: dataset,
                fill: '-1',
            })) || [],
            labels: variant?.datasets[0] || [],
        };
        setData(newChartData);
    }, [
        variant,
    ]);

    const options: any = {
        ...setup.simulation.options,
    };
    options.scales.x.title.text = content.simulation.graph.x;
    options.scales.y.title.text = content.simulation.graph.y;

    const plugins = [
        {
            afterDatasetsDraw: ({ chartArea: { bottom, left, right, top }, ctx }: Chart) => {
                ctx.save();
                ctx.lineWidth = 1;
                ctx.lineCap = 'round';

                // Lines
                ctx.beginPath();
                ctx.moveTo(left, top);
                ctx.lineTo(left, bottom);
                ctx.lineTo(right, bottom);
                ctx.stroke();
                ctx.closePath();

                // X arrow
                ctx.beginPath();
                ctx.moveTo(right, bottom);
                ctx.lineTo(right - 5, bottom - 3);
                ctx.lineTo(right - 5, bottom + 3);
                ctx.fill();
                ctx.stroke();
                ctx.closePath();

                // Y arrow
                ctx.beginPath();
                ctx.moveTo(left, top);
                ctx.lineTo(left + 3, top + 5);
                ctx.lineTo(left - 3, top + 5);
                ctx.fill();
                ctx.stroke();
                ctx.closePath();
            },
            id: 'axisArrows',
        },
    ];

    return <Box bgcolor="white" id="simulationGraph" padding={2} position="relative">
        <Box position="absolute" padding={0.5} right={0} top={0} zIndex={1}>
            <Info color="default" popupName="simulationGraphInfo" />
        </Box>
        <Box display="flex" gap={2} justifyContent="center" marginBottom={1}>
            {setup.simulation.legend.map((label, index) => (
                <Box alignItems="center" display="flex" gap={1} key={index}>
                    <Box bgcolor={setup.simulation.colors[index + 1]} border={1} borderColor="grey.600" display="inline-block" padding={0.75} />
                    <Typography variant="roboto412">
                        {label}
                    </Typography>
                </Box>
            ))}
        </Box>
        <Box position="relative">
            {loading && (
                <Box alignItems="center" bottom={30} display="flex" justifyContent="center" left={20} position="absolute" right={0} top={0} zIndex={1}>
                    <CircularProgress />
                </Box>
            )}
            {data && (
                <Line data={data} options={options} plugins={plugins} />
            )}
        </Box>
    </Box>;

}